import {
  CommunityPost as UsCommunityPostPb,
  CommunityTag as UsCommunityTagPb,
  CreateCommunityPostRequest as UsCreateCommunityPostRequestPb,
  ListCommunityPostsRequest as UsListCommunityPostsRequestPb,
} from "@repo/protobuf/gen/web/public/community_pb";
import Protobuf, { PartialMessage } from "@repo/protobuf/utils";

import CommunityProfile from "./CommunityProfile";

type CommunityPost = {
  id: CommunityPost.Id;
  authorId: CommunityProfile.Id;
  parentId?: CommunityPost.Id;
  replyIds: CommunityPost.Id[];
  title: string;
  bodyMd: string;
  postedAt: Date;
  followCount: number;
  isPinned: boolean;
  upvoteCount: number;
  downvoteCount: number;
  tagIds: CommunityPost.Tag.Id[];
  active: boolean;
};

namespace CommunityPost {
  export type Id = `cpo_${string}`;
  export type Params = {
    parentId?: CommunityPost.Id;
    anonymous: boolean;
    showScore: boolean;
    title?: string;
    bodyMd: string;
    tagIds?: CommunityPost.Tag.Id[];
  };

  export type Vote = "upvote" | "downvote" | null;

  export type SortMethod = (typeof SortMethod.list)[number];

  export namespace SortMethod {
    export const list = ["default", "votes", "recent"] as const;
    export const format = (() => {
      const map: Record<SortMethod, string> = {
        default: "Best",
        votes: "Votes",
        recent: "Newest",
      };
      return (method: SortMethod) => map[method] || map.default;
    })();
  }

  export type Tag = {
    id: CommunityPost.Tag.Id;
    name: string;
    color: string;
  };

  export namespace Tag {
    export type Id = `ctg_${string}`;
  }
}

export default CommunityPost;

export namespace CommunityPostUsa {
  export const normalize = (post: UsCommunityPostPb): CommunityPost => {
    return {
      id: post.token as CommunityPost.Id,
      parentId: post.parentToken as CommunityPost.Id,
      authorId: post.profile!.token as CommunityProfile.Id,
      title: post.title,
      bodyMd: post.bodyMd,
      postedAt: Protobuf.Timestamp.toDate(post.postedAt!),
      followCount: post.followCount,
      replyIds: post.replyTokens as CommunityPost.Id[],
      isPinned: post.isPinned,
      upvoteCount: post.upvoteCount,
      downvoteCount: post.downvoteCount,
      tagIds: post.tagTokens as CommunityPost.Tag.Id[],
      active: post.active,
    };
  };
  export namespace Params {
    export const denormalize = (
      params: CommunityPost.Params
    ): PartialMessage<UsCreateCommunityPostRequestPb> => {
      return {
        parentToken: params.parentId,
        anonymous: params.anonymous,
        showScore: params.showScore,
        bodyMd: params.bodyMd,
        title: params.title,
        tagTokens: params.tagIds,
      };
    };
  }
  export namespace Vote {
    export const normalize = (() => {
      const map: Record<UsCommunityPostPb.Vote, CommunityPost.Vote> = {
        [UsCommunityPostPb.Vote.NONE]: null,
        [UsCommunityPostPb.Vote.DOWNVOTE]: "downvote",
        [UsCommunityPostPb.Vote.UPVOTE]: "upvote",
      };
      return (vote: UsCommunityPostPb.Vote) => map[vote];
    })();
    export const denormalize = (() => {
      const map: Record<`${CommunityPost.Vote}`, UsCommunityPostPb.Vote> = {
        null: UsCommunityPostPb.Vote.NONE,
        downvote: UsCommunityPostPb.Vote.DOWNVOTE,
        upvote: UsCommunityPostPb.Vote.UPVOTE,
      };
      return (vote: CommunityPost.Vote) => map[`${vote}`];
    })();
  }

  export namespace SortMethod {
    export const normalize = (() => {
      const map: Record<
        UsListCommunityPostsRequestPb.SortMethod,
        CommunityPost.SortMethod
      > = {
        [UsListCommunityPostsRequestPb.SortMethod.DEFAULT]: "default",
        [UsListCommunityPostsRequestPb.SortMethod.VOTES]: "votes",
        [UsListCommunityPostsRequestPb.SortMethod.RECENT]: "recent",
      };
      return (method: UsListCommunityPostsRequestPb.SortMethod) =>
        method in map ? map[method] : "default";
    })();

    export const denormalize = (() => {
      const map: Record<
        CommunityPost.SortMethod,
        UsListCommunityPostsRequestPb.SortMethod
      > = {
        default: UsListCommunityPostsRequestPb.SortMethod.DEFAULT,
        votes: UsListCommunityPostsRequestPb.SortMethod.VOTES,
        recent: UsListCommunityPostsRequestPb.SortMethod.RECENT,
      };
      return (method: CommunityPost.SortMethod) =>
        method in map
          ? map[method]
          : UsListCommunityPostsRequestPb.SortMethod.DEFAULT;
    })();
  }

  export namespace Tag {
    export const normalize = (tag: UsCommunityTagPb): CommunityPost.Tag => {
      return {
        id: tag.token as CommunityPost.Tag.Id,
        name: tag.name,
        color: tag.color,
      };
    };

    export const denormalize = (
      tag: CommunityPost.Tag
    ): PartialMessage<UsCommunityTagPb> => {
      return {
        token: tag.id,
        name: tag.name,
        color: tag.color,
      };
    };
  }
}
